import React, { useState } from 'react';
import { Link } from 'react-router-dom';

interface ChineseBuildingOwnersProps {
    onBack: () => void;
}

interface OwnerCompany {
    name: string;
    nameZh: string;
    portfolioSize: string;
    flagshipProperties: string[];
    contactPerson: {
        name: string;
        title: string;
        email: string;
        phone: string;
    };
    status: 'Target' | 'Contacted' | 'LOI Sent' | 'LOI Signed' | 'Negotiating' | 'Client';
    notes: string;
    website?: string;
}

const ownerCompanies: OwnerCompany[] = [
    {
        name: 'China Overseas Land & Investment',
        nameZh: '中国海外发展有限公司',
        portfolioSize: '10M+ sq ft',
        flagshipProperties: ['One Manhattan West', 'Hudson Yards Tower'],
        contactPerson: {
            name: 'Li Wei',
            title: 'Director of US Operations',
            email: 'li.wei@coligroup.com',
            phone: '(212) 555-1234',
        },
        status: 'Target',
        notes: 'Major Chinese SOE with large NYC presence. Interested in ESG and compliance.',
        website: 'https://www.coli.com.hk',
    },
    {
        name: 'HNA Group',
        nameZh: '海航集团',
        portfolioSize: '5M+ sq ft',
        flagshipProperties: ['245 Park Avenue', '850 Third Avenue'],
        contactPerson: {
            name: 'Chen Ming',
            title: 'Asset Manager',
            email: 'chen.ming@hna.com',
            phone: '(212) 555-5678',
        },
        status: 'Contacted',
        notes: 'Asset-heavy, looking for cost savings and regulatory compliance.',
        website: 'https://www.hnagroup.com',
    },
    {
        name: 'Greenland Holdings',
        nameZh: '绿地控股集团',
        portfolioSize: '3M+ sq ft',
        flagshipProperties: ['Pacific Park Brooklyn'],
        contactPerson: {
            name: 'Wang Yu',
            title: 'VP, US Investments',
            email: 'wang.yu@greenland.com',
            phone: '(718) 555-9012',
        },
        status: 'LOI Sent',
        notes: 'Sent LOI, awaiting response. Strong interest in green building tech.',
        website: 'https://www.greenlandsc.com',
    },
    {
        name: 'Bank of China',
        nameZh: '中国银行',
        portfolioSize: '2M+ sq ft',
        flagshipProperties: ['7 Bryant Park'],
        contactPerson: {
            name: 'Zhao Lin',
            title: 'Real Estate Investment Lead',
            email: 'zhao.lin@boc.com',
            phone: '(212) 555-3456',
        },
        status: 'LOI Signed',
        notes: 'LOI signed, moving to contract negotiation.',
        website: 'https://www.boc.cn',
    },
    {
        name: 'Fosun International',
        nameZh: '复星国际',
        portfolioSize: '1.5M+ sq ft',
        flagshipProperties: ['28 Liberty Street'],
        contactPerson: {
            name: 'Sun Jie',
            title: 'Managing Director',
            email: 'sun.jie@fosun.com',
            phone: '(212) 555-7890',
        },
        status: 'Negotiating',
        notes: 'Negotiating terms, interested in pilot project.',
        website: 'https://www.fosun.com',
    },
];

const ChineseBuildingOwners: React.FC<ChineseBuildingOwnersProps> = ({ onBack }) => {
    const [activeTab, setActiveTab] = useState<'owners' | 'value' | 'loi'>('owners');
    const [language, setLanguage] = useState<'en' | 'zh'>('en');

    const toggleLanguage = () => {
        setLanguage(prev => prev === 'en' ? 'zh' : 'en');
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-neutral-50 to-primary-50">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Language Toggle & Header */}
                <div className="flex justify-between items-center mb-8">
                    <button
                        onClick={onBack}
                        className="flex items-center space-x-2 text-primary-600 hover:text-primary-700 transition-colors"
                    >
                        <span>←</span>
                        <span>{language === 'en' ? 'Back' : '返回'}</span>
                    </button>
                    
                    <div className="flex items-center space-x-4">
                        <button
                            onClick={() => setLanguage(language === 'en' ? 'zh' : 'en')}
                            className="bg-white border border-primary-200 px-4 py-2 rounded-lg hover:bg-primary-50 transition-colors flex items-center space-x-2 shadow-sm"
                        >
                            <span>{language === 'en' ? '🇨🇳' : '🇺🇸'}</span>
                            <span className="font-medium">
                                {language === 'en' ? '中文' : 'English'}
                            </span>
                        </button>
                    </div>
                </div>

                {/* Hero Section */}
                <div className="text-center mb-12">
                    <h1 className="text-4xl md:text-5xl font-bold text-primary-800 mb-4">
                        {language === 'en' ? 'Chinese Building Owners' : '中国建筑业主服务'}
                    </h1>
                    <p className="text-xl text-neutral-600 max-w-4xl mx-auto leading-relaxed">
                        {language === 'en' 
                            ? 'Comprehensive services for Chinese real estate investors in NYC. Navigate Local Law 97 compliance and maximize your property investments with expert guidance.'
                            : '为纽约市中国房地产投资者提供全面服务。在专业指导下，轻松应对地方法97合规要求，最大化您的房产投资回报。'
                        }
                    </p>
                    
                    {/* Value Proposition Cards */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
                        <div className="bg-white rounded-xl shadow-lg p-6 border border-primary-100">
                            <div className="text-2xl mb-3">⚖️</div>
                            <h3 className="font-bold text-primary-800 mb-2">
                                {language === 'en' ? 'LL97 Compliance' : '法规合规'}
                            </h3>
                            <p className="text-neutral-600 text-sm">
                                {language === 'en' 
                                    ? 'Expert guidance on NYC Local Law 97 requirements'
                                    : '纽约地方法97要求的专业指导'
                                }
                            </p>
                        </div>
                        <div className="bg-white rounded-xl shadow-lg p-6 border border-secondary-100">
                            <div className="text-2xl mb-3">💰</div>
                            <h3 className="font-bold text-secondary-800 mb-2">
                                {language === 'en' ? 'Cost Optimization' : '成本优化'}
                            </h3>
                            <p className="text-neutral-600 text-sm">
                                {language === 'en' 
                                    ? 'Reduce fines and maximize energy efficiency ROI'
                                    : '减少罚款，最大化能效投资回报'
                                }
                            </p>
                        </div>
                        <div className="bg-white rounded-xl shadow-lg p-6 border border-accent-100">
                            <div className="text-2xl mb-3">🤝</div>
                            <h3 className="font-bold text-accent-800 mb-2">
                                {language === 'en' ? 'Partnership Network' : '合作网络'}
                            </h3>
                            <p className="text-neutral-600 text-sm">
                                {language === 'en' 
                                    ? 'Access to vetted contractors and financing options'
                                    : '接入经验证的承包商和融资选择'
                                }
                            </p>
                        </div>
                    </div>
                </div>

                {/* Tab Navigation */}
                <div className="bg-white rounded-xl shadow-lg mb-8 border border-neutral-200">
                    <div className="border-b border-neutral-200">
                        <nav className="flex">
                            <button
                                onClick={() => setActiveTab('owners')}
                                className={`px-6 py-4 text-sm font-medium transition-colors ${
                                    activeTab === 'owners'
                                        ? 'border-b-2 border-primary-500 text-primary-600 bg-primary-50'
                                        : 'text-neutral-500 hover:text-neutral-700 hover:bg-neutral-50'
                                }`}
                            >
                                🏢 {language === 'en' ? 'Owner Companies' : '公司业主'}
                            </button>
                            <button
                                onClick={() => setActiveTab('value')}
                                className={`px-6 py-4 text-sm font-medium transition-colors ${
                                    activeTab === 'value'
                                        ? 'border-b-2 border-primary-500 text-primary-600 bg-primary-50'
                                        : 'text-neutral-500 hover:text-neutral-700 hover:bg-neutral-50'
                                }`}
                            >
                                💡 {language === 'en' ? 'Value Proposition' : '价值主张'}
                            </button>
                            <button
                                onClick={() => setActiveTab('loi')}
                                className={`px-6 py-4 text-sm font-medium transition-colors ${
                                    activeTab === 'loi'
                                        ? 'border-b-2 border-primary-500 text-primary-600 bg-primary-50'
                                        : 'text-neutral-500 hover:text-neutral-700 hover:bg-neutral-50'
                                }`}
                            >
                                📝 {language === 'en' ? 'LOI Tracker' : '意向书跟踪'}
                            </button>
                        </nav>
                    </div>

                    <div className="p-8">
                        {/* Owner Companies Tab */}
                        {activeTab === 'owners' && (
                            <div>
                                <h2 className="text-2xl font-semibold text-gray-900 mb-6">
                                    {language === 'en' ? 'Major Chinese Owner Companies' : '主要中国业主公司'}
                                </h2>
                                <div className="space-y-6">
                                    {ownerCompanies.map((company, idx) => (
                                        <div key={idx} className="bg-gray-50 rounded-lg p-6 border border-gray-200">
                                            <div className="flex justify-between items-start mb-4">
                                                <div className="flex-1">
                                                    <div className="flex items-center gap-3 mb-2">
                                                        <div>
                                                            <h3 className="text-xl font-bold text-gray-900">
                                                                {language === 'en' ? company.name : company.nameZh}
                                                            </h3>
                                                            {language === 'en' ? (
                                                                <p className="text-sm text-gray-500">{company.nameZh}</p>
                                                            ) : (
                                                                <p className="text-sm text-gray-500">{company.name}</p>
                                                            )}
                                                        </div>
                                                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                                                            company.status === 'Target' ? 'bg-gray-100 text-gray-800' :
                                                            company.status === 'Contacted' ? 'bg-blue-100 text-blue-800' :
                                                            company.status === 'LOI Sent' ? 'bg-yellow-100 text-yellow-800' :
                                                            company.status === 'LOI Signed' ? 'bg-green-100 text-green-800' :
                                                            company.status === 'Negotiating' ? 'bg-orange-100 text-orange-800' :
                                                            'bg-gray-100 text-gray-800'
                                                        }`}>
                                                            {language === 'en' ? company.status : 
                                                                company.status === 'Target' ? '目标' :
                                                                company.status === 'Contacted' ? '已联系' :
                                                                company.status === 'LOI Sent' ? '已发送意向书' :
                                                                company.status === 'LOI Signed' ? '意向书已签署' :
                                                                company.status === 'Negotiating' ? '商谈中' : '客户'
                                                            }
                                                        </span>
                                                    </div>
                                                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                                                        <div>
                                                            <span className="text-sm font-medium text-gray-600">
                                                                {language === 'en' ? 'Portfolio Size:' : '投资组合规模:'}
                                                            </span>
                                                            <p className="text-gray-900 font-medium">{company.portfolioSize}</p>
                                                        </div>
                                                        <div>
                                                            <span className="text-sm font-medium text-gray-600">
                                                                {language === 'en' ? 'Flagship Properties:' : '旗舰物业:'}
                                                            </span>
                                                            <ul className="text-gray-900 text-sm list-disc ml-4">
                                                                {company.flagshipProperties.map((prop, i) => (
                                                                    <li key={i}>{prop}</li>
                                                                ))}
                                                            </ul>
                                                        </div>
                                                        <div>
                                                            <span className="text-sm font-medium text-gray-600">
                                                                {language === 'en' ? 'Contact:' : '联系人:'}
                                                            </span>
                                                            <p className="text-gray-900 font-medium">{company.contactPerson.name}</p>
                                                            <p className="text-gray-600 text-sm">
                                                                {language === 'en' ? company.contactPerson.title : 
                                                                    company.contactPerson.title === 'Director of US Operations' ? '美国业务总监' :
                                                                    company.contactPerson.title === 'Asset Manager' ? '资产经理' :
                                                                    company.contactPerson.title === 'VP, US Investments' ? '美国投资副总裁' :
                                                                    company.contactPerson.title === 'Real Estate Investment Lead' ? '房地产投资主管' :
                                                                    company.contactPerson.title === 'Managing Director' ? '董事总经理' :
                                                                    company.contactPerson.title
                                                                }
                                                            </p>
                                                            <p className="text-gray-600 text-sm">{company.contactPerson.email}</p>
                                                            <p className="text-gray-600 text-sm">{company.contactPerson.phone}</p>
                                                        </div>
                                                        <div>
                                                            <span className="text-sm font-medium text-gray-600">
                                                                {language === 'en' ? 'Website:' : '网站:'}
                                                            </span>
                                                            {company.website && (
                                                                <a href={company.website} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline text-sm">
                                                                    {company.website.replace('https://', '')}
                                                                </a>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <div className="bg-gray-100 rounded-lg p-3 mb-2">
                                                        <p className="text-sm text-gray-700">
                                                            {language === 'en' ? company.notes : 
                                                                company.name === 'China Overseas Land & Investment' ? '大型中国国有企业，在纽约市有大量地产。对ESG和合规有兴趣。' :
                                                                company.name === 'HNA Group' ? '资产丰富，寻求成本节约和监管合规。' :
                                                                company.name === 'Greenland Holdings' ? '已发送意向书，等待回复。对绿色建筑技术有浓厚兴趣。' :
                                                                company.name === 'Bank of China' ? '意向书已签署，进入合同谈判阶段。' :
                                                                company.name === 'Fosun International' ? '正在商谈条款，对试点项目有兴趣。' :
                                                                company.notes
                                                            }
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Value Proposition Tab */}
                        {activeTab === 'value' && (
                            <div>
                                <h2 className="text-2xl font-semibold text-gray-900 mb-6">
                                    {language === 'en' ? 'Why Work With Us?' : '为什么选择我们？'}
                                </h2>
                                <div className="bg-white rounded-lg p-6 border border-gray-200 mb-6">
                                    <ul className="space-y-4">
                                        <li className="flex items-start">
                                            <span className="text-green-500 mr-2 mt-1">✓</span>
                                            <div>
                                                {language === 'en' 
                                                    ? <span>LL97 compliance made easy—avoid millions in fines</span>
                                                    : <span><span className="font-semibold">LL97合规变得简单</span> - 避免数百万罚款</span>
                                                }
                                                {language !== 'en' && <div className="text-sm text-gray-500 mt-1">轻松实现LL97合规，避免高额罚款</div>}
                                            </div>
                                        </li>
                                        <li className="flex items-start">
                                            <span className="text-green-500 mr-2 mt-1">✓</span>
                                            <div>
                                                {language === 'en' 
                                                    ? <span>Turnkey retrofit project management and financing</span>
                                                    : <span><span className="font-semibold">一站式改造项目管理和融资</span></span>
                                                }
                                                {language !== 'en' && <div className="text-sm text-gray-500 mt-1">全方位的改造项目管理与融资方案</div>}
                                            </div>
                                        </li>
                                        <li className="flex items-start">
                                            <span className="text-green-500 mr-2 mt-1">✓</span>
                                            <div>
                                                {language === 'en' 
                                                    ? <span>Enhance ESG profile for global investors</span>
                                                    : <span><span className="font-semibold">提升ESG评级</span>，吸引全球投资者</span>
                                                }
                                                {language !== 'en' && <div className="text-sm text-gray-500 mt-1">提升环境、社会和治理表现，增加投资吸引力</div>}
                                            </div>
                                        </li>
                                        <li className="flex items-start">
                                            <span className="text-green-500 mr-2 mt-1">✓</span>
                                            <div>
                                                {language === 'en' 
                                                    ? <span>Chinese language support and dedicated account team</span>
                                                    : <span><span className="font-semibold">中文支持</span>和专属客户团队</span>
                                                }
                                                {language !== 'en' && <div className="text-sm text-gray-500 mt-1">提供全中文服务和专属客户经理，沟通无障碍</div>}
                                            </div>
                                        </li>
                                        <li className="flex items-start">
                                            <span className="text-green-500 mr-2 mt-1">✓</span>
                                            <div>
                                                {language === 'en' 
                                                    ? <span>Proven results with NYC's largest portfolios</span>
                                                    : <span><span className="font-semibold">在纽约市最大物业组合中的成功案例</span></span>
                                                }
                                                {language !== 'en' && <div className="text-sm text-gray-500 mt-1">拥有众多纽约大型物业改造成功经验</div>}
                                            </div>
                                        </li>
                                    </ul>
                                </div>
                                <div className="bg-blue-50 rounded-lg p-6">
                                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                                        {language === 'en' ? 'Our Ask' : '我们的请求'}
                                    </h3>
                                    <p className="text-gray-700 mb-2">
                                        {language === 'en' 
                                            ? 'Sign a non-binding Letter of Intent (LOI) to explore a pilot retrofit project. We\'ll handle the rest.'
                                            : '签署无约束力的意向书(LOI)，探索试点改造项目。剩下的事情我们来处理。'
                                        }
                                    </p>
                                    <div className="flex space-x-4">
                                        <a
                                            href="/sample-loi-chinese-owner.pdf"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="inline-block bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded text-sm transition-colors mt-2"
                                        >
                                            {language === 'en' ? 'Download Sample LOI (PDF)' : '下载意向书样本 (PDF)'}
                                        </a>
                                        <button
                                            className="inline-block bg-green-600 hover:bg-green-700 text-white py-2 px-4 rounded text-sm transition-colors mt-2"
                                        >
                                            {language === 'en' ? 'Schedule Consultation' : '预约咨询'}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* LOI Tracker Tab */}
                        {activeTab === 'loi' && (
                            <div>
                                <h2 className="text-2xl font-semibold text-gray-900 mb-6">
                                    {language === 'en' ? 'LOI Tracker' : '意向书跟踪'}
                                </h2>
                                <div className="space-y-6">
                                    {ownerCompanies.filter(c => c.status === 'LOI Sent' || c.status === 'LOI Signed' || c.status === 'Negotiating' || c.status === 'Client').map((company, idx) => (
                                        <div key={idx} className="bg-green-50 rounded-lg p-6 border border-green-200">
                                            <div className="flex justify-between items-center mb-2">
                                                <h3 className="text-xl font-bold text-green-900">
                                                    {language === 'en' ? company.name : company.nameZh}
                                                </h3>
                                                <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                                                    company.status === 'LOI Sent' ? 'bg-yellow-100 text-yellow-800' :
                                                    company.status === 'LOI Signed' ? 'bg-green-100 text-green-800' :
                                                    company.status === 'Negotiating' ? 'bg-orange-100 text-orange-800' :
                                                    'bg-gray-100 text-gray-800'
                                                }`}>
                                                    {language === 'en' ? company.status : 
                                                        company.status === 'LOI Sent' ? '已发送意向书' :
                                                        company.status === 'LOI Signed' ? '意向书已签署' :
                                                        company.status === 'Negotiating' ? '商谈中' : '客户'
                                                    }
                                                </span>
                                            </div>
                                            <div className="mb-2">
                                                <span className="text-sm font-medium text-gray-600">
                                                    {language === 'en' ? 'Contact:' : '联系人:'}
                                                </span>
                                                <span className="ml-2 text-gray-900 font-medium">{company.contactPerson.name}</span>
                                                <span className="ml-2 text-gray-600 text-sm">{company.contactPerson.email}</span>
                                            </div>
                                            <div className="mb-2">
                                                <span className="text-sm font-medium text-gray-600">
                                                    {language === 'en' ? 'Flagship Properties:' : '旗舰物业:'}
                                                </span>
                                                <span className="ml-2 text-gray-900">{company.flagshipProperties.join(', ')}</span>
                                            </div>
                                            <div className="bg-gray-100 rounded-lg p-3">
                                                <p className="text-sm text-gray-700">
                                                    {language === 'en' ? company.notes : 
                                                        company.name === 'Greenland Holdings' ? '已发送意向书，等待回复。对绿色建筑技术有浓厚兴趣。' :
                                                        company.name === 'Bank of China' ? '意向书已签署，进入合同谈判阶段。' :
                                                        company.name === 'Fosun International' ? '正在商谈条款，对试点项目有兴趣。' :
                                                        company.notes
                                                    }
                                                </p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Call to Action Section */}
                <div className="bg-gradient-to-r from-primary-600 to-primary-700 rounded-2xl shadow-xl p-8 mb-12 text-white">
                    <div className="text-center">
                        <h2 className="text-3xl font-bold mb-4">
                            {language === 'en' ? 'Ready to Get Started?' : '准备开始了吗？'}
                        </h2>
                        <p className="text-xl text-primary-100 mb-6 max-w-2xl mx-auto">
                            {language === 'en' 
                                ? 'Join our network of successful Chinese property owners who are already saving millions through LL97 compliance.'
                                : '加入我们成功的中国业主网络，他们已经通过LL97合规节省了数百万美元。'
                            }
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <button className="bg-white text-primary-700 hover:bg-primary-50 px-8 py-3 rounded-lg font-semibold transition-colors shadow-lg">
                                {language === 'en' ? '📞 Schedule Free Consultation' : '📞 预约免费咨询'}
                            </button>
                            <button className="border-2 border-white text-white hover:bg-white hover:text-primary-700 px-8 py-3 rounded-lg font-semibold transition-colors">
                                {language === 'en' ? '📧 Download Investment Guide' : '📧 下载投资指南'}
                            </button>
                        </div>
                    </div>
                </div>

                {/* Quick Actions */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <Link
                        to="/ll97-calculator"
                        className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-all border border-neutral-200 hover:border-primary-300"
                    >
                        <div className="text-center">
                            <div className="text-2xl mb-4">🧮</div>
                            <h3 className="text-lg font-semibold text-neutral-900 mb-2">
                                {language === 'en' ? 'Calculate LL97 Fines' : '计算LL97罚款'}
                            </h3>
                            <p className="text-sm text-neutral-600">
                                {language === 'en' ? 'See potential fines for your properties' : '查看您物业的潜在罚款'}
                            </p>
                        </div>
                    </Link>
                    <Link
                        to="/retrofit-calculator"
                        className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-all border border-neutral-200 hover:border-secondary-300"
                    >
                        <div className="text-center">
                            <div className="text-2xl mb-4">🔧</div>
                            <h3 className="text-lg font-semibold text-neutral-900 mb-2">
                                {language === 'en' ? 'Retrofit ROI Calculator' : '改造投资回报计算器'}
                            </h3>
                            <p className="text-sm text-neutral-600">
                                {language === 'en' ? 'Calculate energy savings potential' : '计算节能潜力'}
                            </p>
                        </div>
                    </Link>
                    <Link
                        to="/building-analysis"
                        className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-all border border-neutral-200 hover:border-accent-300"
                    >
                        <div className="text-center">
                            <div className="text-2xl mb-4">🏢</div>
                            <h3 className="text-lg font-semibold text-neutral-900 mb-2">
                                {language === 'en' ? 'Building Analysis' : '建筑分析'}
                            </h3>
                            <p className="text-sm text-neutral-600">
                                {language === 'en' ? 'Detailed property analysis & recommendations' : '详细的物业分析和建议'}
                            </p>
                        </div>
                    </Link>
                </div>
            </div>
        </div>
    );
};

export default ChineseBuildingOwners;
